/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";
import { sanitizeUrl } from "@braintree/sanitize-url";

/* ────────────────────────────────────────────────────────────────────────── */
/* Environment & helpers                                                      */
/* ────────────────────────────────────────────────────────────────────────── */
const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const PDFCtor = (typeof globalThis !== "undefined" && (globalThis.PDFNode || globalThis.PDF)) || undefined;

const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

/* ────────────────────────────────────────────────────────────────────────── */
/* Public API: properties                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export const PDF_PROPS = ["src", "openAs2D", "networkable"];

/* ────────────────────────────────────────────────────────────────────────── */
/* Editor / plain-Three adapter                                               */
/* ────────────────────────────────────────────────────────────────────────── */
class PDFEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    PDF_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(sourceObject, v);
            } catch {
              /* ignore */
            }
          } else {
            try {
              sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed initial value (best-effort, avoid triggering setters)
      const d = originalDescriptors.get(prop);
      let initial;
      try {
        if (d?.get) initial = d.get.call(sourceObject);
        else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) initial = sourceObject[prop];
      } catch {
        /* ignore */
      }
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* A-Frame adapter                                                            */
/* ────────────────────────────────────────────────────────────────────────── */
class PDFAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const MEDIA_LOADER_COMPONENT = "media-loader";
    const NETWORKED_COMPONENT = "networked";

    const _pendingUpdates = {};

    const _flushPending = () => {
      if (!this.sourceObject?.el) return;
      const current = {
        ...(this.sourceObject.el.getAttribute(MEDIA_LOADER_COMPONENT) || {})
      };

      Object.keys(_pendingUpdates).forEach(k => {
        if (k === "mediaOptions") return;
        current[k] = _pendingUpdates[k];
      });

      if (_pendingUpdates.mediaOptions) {
        current.mediaOptions = {
          ...(current.mediaOptions || {}),
          ..._pendingUpdates.mediaOptions
        };
      }

      Object.keys(_pendingUpdates).forEach(k => delete _pendingUpdates[k]);
      this.sourceObject.el.setAttribute(MEDIA_LOADER_COMPONENT, current);
    };

    if (this.sourceObject?.el) {
      this.sourceObject.el.addEventListener("componentinitialized", e => {
        if (e?.detail?.name === MEDIA_LOADER_COMPONENT) _flushPending();
      });
    }

    const setMediaLoaderProp = (key, val, inMediaOptions = false) => {
      if (!this.sourceObject?.el) return;

      const attr = this.sourceObject.el.getAttribute(MEDIA_LOADER_COMPONENT);

      if (!attr) {
        if (inMediaOptions) {
          _pendingUpdates.mediaOptions = {
            ...(_pendingUpdates.mediaOptions || {}),
            [key]: val
          };
        } else {
          _pendingUpdates[key] = val;
        }
        return;
      }

      const current = { ...attr };
      if (inMediaOptions) {
        current.mediaOptions = { ...(current.mediaOptions || {}), [key]: val };
      } else {
        current[key] = val;
      }
      this.sourceObject.el.setAttribute(MEDIA_LOADER_COMPONENT, current);
    };

    const getMediaLoaderProp = (key, inMediaOptions = false) => {
      const attr = this.sourceObject?.el?.getAttribute(MEDIA_LOADER_COMPONENT);
      if (!attr) return undefined;
      return inMediaOptions ? attr.mediaOptions?.[key] : attr[key];
    };

    // src (top-level)
    Object.defineProperty(this, "src", {
      get: () => getMediaLoaderProp("src"),
      set: v => {
        setMediaLoaderProp("src", v);
      }
    });

    // openAs2D in mediaOptions
    Object.defineProperty(this, "openAs2D", {
      get: () => getMediaLoaderProp("openAs2D", true),
      set: v => {
        setMediaLoaderProp("openAs2D", v, true);
      }
    });

    // networkable boolean -------------------------------------------
    Object.defineProperty(this, "networkable", {
      get: () => this.sourceObject?.el?.hasAttribute("networked"),
      set: v => {
        if (!this.sourceObject?.el) return;
        if (v) {
          if (!this.sourceObject.el.hasAttribute("networked")) {
            this.sourceObject.el.setAttribute("networked", {
              template: "#static-controlled-media"
            });
          }
        } else {
          this.sourceObject.el.removeAttribute("networked");
        }
      }
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Lazy adapter bootstrap (works for adopted instances)                       */
/* ────────────────────────────────────────────────────────────────────────── */
function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of PDF_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new PDFAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", { value: new PDFEditorAdapter(self, descriptors), configurable: true });
  return self._adapter;
}

export class PDFElement extends ScriptableObject {
  static scriptableType = "pdf";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");

      // PDFs are always controlled media
      if (initial.networkable !== false) {
        el.setAttribute("networked", {
          template: "#static-controlled-media"
        });
      }

      // Add media-loader component with PDF configuration
      const mediaOptions = { openAs2D: initial.openAs2D };

      el.setAttribute("media-loader", {
        src: sanitizeUrl(initial.src || ""),
        fitToBox: false,
        resolve: true,
        fileIsOwned: true,
        animate: false,
        mediaOptions,
        moveTheParentNotTheMesh: true,
        useFancyLoader: false
      });

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real PDFNode and upgrade to scriptable
    const node = createPDFElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public pdf props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      
      // deep-clone where necessary to avoid shared refs
      if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else if (val && typeof val.clone === "function") {
        this[prop] = val.clone();
      } else {
        this[prop] = val;
      }
    };

    for (const p of PDF_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing logical children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const srcChild of logicalChildrenOf(source)) {
        this.add(srcChild.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of PDF_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      // Follow the same pattern as other implementations
      if (Array.isArray(v)) {
        init[p] = v.slice();
      } else if (v && typeof v.clone === "function") {
        init[p] = v.clone();
      } else {
        init[p] = v;
      }
    }

    const dest = new PDFElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// DRY helper: upgrade a plain Editor PDFNode instance to Scriptable, attach adapter and proxies
function makeEditorPDFNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || PDFCtor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) Object.defineProperty(node, "scriptableType", { value: PDFElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = PDFElement.scriptableType;
  }

  // Attach adapter and per-instance property proxies
  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new PDFEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new PDFEditorAdapter(node, descriptors);
  }

  for (const prop of PDF_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* ignore define errors */
    }
  }

  // Ensure PDFElement's enhanced `clone` logic is used for
  // editor-created pdf nodes. Without this, the instance would fall back to
  // PDFNode (or Object3D) defaults which do not propagate the
  // scriptable properties managed by the adapter.
  try {
    if (node.clone !== PDFElement.prototype.clone) {
      Object.defineProperty(node, "clone", {
        value: PDFElement.prototype.clone,
        writable: true,
        configurable: true
      });
    }
  } catch {
    node.clone = PDFElement.prototype.clone;
  }

  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Bind property surface (works for adopted instances too)                    */
/* ────────────────────────────────────────────────────────────────────────── */
PDF_PROPS.forEach(prop => {
  Object.defineProperty(PDFElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* ────────────────────────────────────────────────────────────────────────── */
/* Optional editor factory helper                                             */
/* ────────────────────────────────────────────────────────────────────────── */
export function createPDFElementEditor(initial = {}) {
  const PDFCtor = (typeof globalThis !== "undefined" && (globalThis.PDFNode || globalThis.PDF)) || undefined;
  
  if (typeof PDFCtor !== "function") {
    throw new Error(
      "PDFElementEditor: PDFNode constructor not found – cannot create element in editor runtime."
    );
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new PDFCtor(editor);
  } catch (e) {
    throw new Error(
      `PDFElementEditor: failed to construct PDFNode – ${e?.message || e}`
    );
  }

  makeEditorPDFNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) node[k] = initial[k];
  }
  
  return node;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Registry: promote A-Frame elements and pure-Three objects                  */
/* ────────────────────────────────────────────────────────────────────────── */
ScriptableObjects.registerType("pdf", source => {
  if (!source) return source;

  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;

  if (type !== PDFElement.scriptableType) return source;

  // A-Frame entities: use base adopt via class method
  if (isAFrameEntity(source)) return PDFElement.adopt(source);

  // Editor/plain-Three nodes: upgrade in-place via shared helper
  if (isObject3D(source)) return makeEditorPDFNodeScriptable(source);

  return source;
});

/* Named exports expected by external consumers */
export { PDFAFrameAdapter, PDFEditorAdapter };

/* Optional: nicer DevTools label */
Object.defineProperty(PDFElement.prototype, Symbol.toStringTag, { value: "PDFElement" });